/* eslint-disable no-unused-vars */
import { ReferenceDataStatus } from "@/lib/interfaces and Enums/ENUMReferenceDataStatus";
import { SWAP_REFERENCE_LISTS } from "@/lib/interfaces and Enums/ENUMReferenceList";
import { getAllBondSecurities } from "@/store/bondSecuritiesStoreACTIONS";
import { getAllFutureSecurities } from "@/store/futureSecuritiesStoreACTIONS";
import { getAllPortfolios } from "@/store/portfoliosStoreACTIONS";
import { getReferenceLists } from "@/store/referenceListsStoreACTIONS";
import { getAllSwapSecurities } from "@/store/swapSecuritiesStoreACTIONS";

// LQC-2254 - the widget cannot take input until it knows which portfolios and which
// securities the back end will accept, so both endpoints are fetched together on
// mount and the widget is gated until they BOTH resolve.

export const REFERENCE_DATA_LOADING_MESSAGE =
  "Loading portfolios and securities...";
export const REFERENCE_DATA_FAILED_MESSAGE =
  "Could not load portfolios and securities.";

// LQC-2493 - futures load only once a user picks Future, so they get their own pair.
export const FUTURES_REFERENCE_DATA_LOADING_MESSAGE =
  "Loading futures contracts...";
export const FUTURES_REFERENCE_DATA_FAILED_MESSAGE =
  "Could not load futures contracts.";

// What the widget shows while its reference data settles.
export enum ReferenceDataGate {
  Loading = "Loading",
  Ready = "Ready",
  Failed = "Failed",
}

// Idle counts as Loading: the mount effect is about to fire, and flashing the empty
// form for a frame before it does would be worse than waiting. Any error fails the
// gate; every status has to be Loaded for it to open.
export function returnGateForStatuses(
  statuses: ReferenceDataStatus[],
): ReferenceDataGate {
  if (statuses.includes(ReferenceDataStatus.Error)) {
    return ReferenceDataGate.Failed;
  }
  if (statuses.every((status) => status === ReferenceDataStatus.Loaded)) {
    return ReferenceDataGate.Ready;
  }
  return ReferenceDataGate.Loading;
}

export function returnReferenceDataGate(
  portfoliosStatus: ReferenceDataStatus,
  bondSecuritiesStatus: ReferenceDataStatus,
): ReferenceDataGate {
  return returnGateForStatuses([portfoliosStatus, bondSecuritiesStatus]);
}

export function returnReferenceDataMessage(gate: ReferenceDataGate): string {
  if (gate === ReferenceDataGate.Failed) return REFERENCE_DATA_FAILED_MESSAGE;
  if (gate === ReferenceDataGate.Loading) return REFERENCE_DATA_LOADING_MESSAGE;
  return "";
}

export function returnFuturesReferenceDataMessage(
  gate: ReferenceDataGate,
): string {
  if (gate === ReferenceDataGate.Failed) {
    return FUTURES_REFERENCE_DATA_FAILED_MESSAGE;
  }
  if (gate === ReferenceDataGate.Loading) {
    return FUTURES_REFERENCE_DATA_LOADING_MESSAGE;
  }
  return "";
}

// Fired from the widget's mount effect. Both loaders are fetch-once and swallow their
// own failures, so this is safe to call from every widget instance and never throws.
export async function loadTradeFromScratchReferenceData(
  getAccessTokenSilently: Function,
  forceReload = false,
): Promise<void> {
  await Promise.all([
    getAllPortfolios(getAccessTokenSilently, forceReload),
    getAllBondSecurities(getAccessTokenSilently, forceReload),
  ]);
}

// LQC-2493 - fired from the future ticket mount effect, not the widget: a user who only
// ever writes bonds never calls GET /Securities/Futures. Fetch-once like the others.
export async function loadFuturesReferenceData(
  getAccessTokenSilently: Function,
  forceReload = false,
): Promise<void> {
  await getAllFutureSecurities(getAccessTokenSilently, forceReload);
}

// LQC-2925 / LQC-2939 - Swap / Swaption code lists and template swaps. Never gates the
// ticket: each field runs on its fallback until its data lands. Fired by the ticket shells
// and "+ Leg".
export async function loadSwapReferenceData(
  getAccessTokenSilently: Function,
  forceReload = false,
): Promise<void> {
  await Promise.all([
    getReferenceLists(SWAP_REFERENCE_LISTS, getAccessTokenSilently, forceReload),
    getAllSwapSecurities(getAccessTokenSilently, forceReload),
  ]);
}

// Only give up waiting for idle after this long, so a busy page still warms the lists.
export const REFERENCE_PREFETCH_TIMEOUT_MS = 5000;

// Warm the small code lists once the browser is idle. Not the ~750KB templates: those wait
// for a swap ticket. Returns a cancel for the mount effect.
export function prefetchSwapReferenceDataWhenIdle(
  getAccessTokenSilently: Function,
): () => void {
  const run = () => getReferenceLists(SWAP_REFERENCE_LISTS, getAccessTokenSilently);
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(run, {
      timeout: REFERENCE_PREFETCH_TIMEOUT_MS,
    });
    return () => window.cancelIdleCallback(handle);
  }
  const handle = window.setTimeout(run, REFERENCE_PREFETCH_TIMEOUT_MS);
  return () => window.clearTimeout(handle);
}
